import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'

import {
  studioListVideos,
  studioGetVideo,
  studioCreateVideo,
  studioUpdateVideo,
  studioGetAnalytics,
  studioGetVideoComments,
  studioCreateVideoComment,
  studioLikeVideo,
  studioUnlikeVideo,
  studioListArticles,
  studioGetArticle,
  studioCreateArticle,
  studioUpdateArticle,
  studioDeleteArticle,
  type StudioVideoCreateInput,
  type StudioVideoUpdateInput,
  type StudioArticleCreateInput,
  type StudioArticleUpdateInput,
} from '#/lib/api/studio'

const RQKEY_ROOT = 'studio'
export const RQKEY_VIDEOS = [RQKEY_ROOT, 'videos']
export const RQKEY_VIDEOS_PUBLISHED = [RQKEY_ROOT, 'videos', 'published']
export const RQKEY_VIDEO = (id: string) => [RQKEY_ROOT, 'video', id]
export const RQKEY_ANALYTICS = (videoId?: string) =>
  [RQKEY_ROOT, 'analytics', videoId ?? 'all'] as const
export const RQKEY_VIDEO_COMMENTS = (videoId: string) =>
  [RQKEY_ROOT, 'video', 'comments', videoId] as const

export const RQKEY_ARTICLES = [RQKEY_ROOT, 'articles']
export const RQKEY_ARTICLE = (id: string) => [RQKEY_ROOT, 'article', id]

/** Pass ownerDid so only that user's videos are returned. Never fetches without a DID (My Videos must be scoped to a user). */
export function useStudioVideosQuery(opts?: {ownerDid?: string | null}) {
  const ownerDid = opts?.ownerDid
  return useQuery({
    queryKey: [...RQKEY_VIDEOS, ownerDid ?? 'none'],
    queryFn: () =>
      studioListVideos(ownerDid ? {owner_did: ownerDid} : undefined),
    staleTime: 60 * 1000,
    enabled: !!ownerDid, // only fetch when we have a DID so we never get an unfiltered list
  })
}

/** Public video feed: published only, standard format only (excludes short clips). */
export function useStudioPublishedVideosQuery() {
  return useQuery({
    queryKey: RQKEY_VIDEOS_PUBLISHED,
    queryFn: () =>
      studioListVideos({status: 'published', video_format: 'standard'}),
    staleTime: 60 * 1000,
  })
}

const RQKEY_VIDEOS_SEARCH = [RQKEY_ROOT, 'videos', 'search']

/** Search SHXRE long-form videos by query (filters title/description). Empty query = all published standard. */
export function useStudioSearchVideosQuery(
  searchQuery: string,
  opts?: {enabled?: boolean},
) {
  const q = searchQuery.trim().toLowerCase()
  const enabled = opts?.enabled !== false
  return useQuery({
    queryKey: [...RQKEY_VIDEOS_SEARCH, q],
    queryFn: async () => {
      const list = await studioListVideos({
        status: 'published',
        video_format: 'standard',
      })
      if (!q) return list
      return list.filter(v => {
        const title = (v.title ?? '').toLowerCase()
        const desc = (v.description ?? '').toLowerCase()
        return title.includes(q) || desc.includes(q)
      })
    },
    staleTime: 60 * 1000,
    enabled,
  })
}

const RQKEY_VIDEOS_CLIPS = [RQKEY_ROOT, 'videos', 'clips']

/** Published Studio videos with format=short for Clips feed. */
export function useStudioClipsQuery() {
  return useQuery({
    queryKey: RQKEY_VIDEOS_CLIPS,
    queryFn: () =>
      studioListVideos({status: 'published', video_format: 'short'}),
    staleTime: 60 * 1000,
  })
}

export function useStudioVideoQuery(id: string | null) {
  return useQuery({
    queryKey: RQKEY_VIDEO(id ?? ''),
    queryFn: () => (id ? studioGetVideo(id) : Promise.resolve(null)),
    enabled: !!id,
    staleTime: 60 * 1000,
  })
}

export function useStudioAnalyticsQuery(videoId?: string) {
  return useQuery({
    queryKey: RQKEY_ANALYTICS(videoId),
    queryFn: () => studioGetAnalytics(videoId),
    staleTime: 5 * 60 * 1000,
  })
}

export function useStudioCreateVideo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: StudioVideoCreateInput) => studioCreateVideo(input),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEOS})
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEOS_PUBLISHED})
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEOS_CLIPS})
      queryClient.invalidateQueries({queryKey: RQKEY_ANALYTICS()})
    },
  })
}

export function useStudioUpdateVideo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {id: string; input: StudioVideoUpdateInput}) =>
      studioUpdateVideo(id, input),
    onSuccess: (_, {id}) => {
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEOS})
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEOS_PUBLISHED})
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEOS_CLIPS})
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEO(id)})
    },
  })
}

export function useStudioVideoCommentsQuery(videoId: string | null) {
  return useQuery({
    queryKey: RQKEY_VIDEO_COMMENTS(videoId ?? ''),
    queryFn: () => (videoId ? studioGetVideoComments(videoId) : Promise.resolve([])),
    enabled: !!videoId,
    staleTime: 30 * 1000,
  })
}

export function useStudioCreateVideoComment(videoId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: string | {text: string; author_id?: string; author_handle?: string}) =>
      studioCreateVideoComment(
        videoId,
        typeof input === 'string' ? {text: input} : input,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEO_COMMENTS(videoId)})
    },
  })
}

export function useStudioLikeVideo(videoId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => studioLikeVideo(videoId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEOS_CLIPS})
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEO(videoId)})
    },
  })
}

export function useStudioUnlikeVideo(videoId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => studioUnlikeVideo(videoId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEOS_CLIPS})
      queryClient.invalidateQueries({queryKey: RQKEY_VIDEO(videoId)})
    },
  })
}

// --- Articles ---

/** Pass ownerDid so only that user's articles are returned. */
export function useStudioArticlesQuery(opts?: {ownerDid?: string | null; status?: 'draft' | 'published'}) {
  const ownerDid = opts?.ownerDid
  const status = opts?.status
  return useQuery({
    queryKey: [...RQKEY_ARTICLES, ownerDid ?? 'none', status ?? 'all'],
    queryFn: () =>
      studioListArticles(
        ownerDid ? {owner_did: ownerDid, ...(status && {status})} : undefined,
      ),
    staleTime: 60 * 1000,
    enabled: !!ownerDid,
  })
}

export function useStudioArticleQuery(id: string | null) {
  return useQuery({
    queryKey: RQKEY_ARTICLE(id ?? ''),
    queryFn: () => (id ? studioGetArticle(id) : Promise.resolve(null)),
    enabled: !!id,
    staleTime: 60 * 1000,
  })
}

export function useStudioCreateArticle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: StudioArticleCreateInput) => studioCreateArticle(input),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: RQKEY_ARTICLES})
    },
  })
}

export function useStudioUpdateArticle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {id: string; input: StudioArticleUpdateInput}) =>
      studioUpdateArticle(id, input),
    onSuccess: (_, {id}) => {
      queryClient.invalidateQueries({queryKey: RQKEY_ARTICLES})
      queryClient.invalidateQueries({queryKey: RQKEY_ARTICLE(id)})
    },
  })
}

export function useStudioDeleteArticle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => studioDeleteArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: RQKEY_ARTICLES})
    },
  })
}
